// ─── Condition Cascade Logic ──────────────────────────────────────────────────
// Field → Type → Operators → Value Input
// Keys MUST use order_ prefix to match CelBuilder/Go CEL variables.
import { allCountries } from 'country-region-data';

export const FIELD_OPTIONS = [
  { label: 'Order Custom', value: 'order_custom', type: 'text_list' },
  { label: 'Order Internal Notes', value: 'order_internal_notes', type: 'text' },
  { label: 'Order Item Category', value: 'order_item_categories', type: 'text_list' },
  { label: 'Order Item Count', value: 'order_item_count', type: 'number' },
  { label: 'Order Item Custom', value: 'order_item_custom', type: 'text_list' },
  { label: 'Order Item SKU', value: 'order_item_skus', type: 'list' },
  { label: 'Order Item Sale Price', value: 'order_item_sale_prices', type: 'numeric_list' },
  { label: 'Order Shipping Method', value: 'order_shipping_methods', type: 'text_list' },
  { label: 'Order Status', value: 'order_status', type: 'status' },
  { label: 'Order Tags (existing)', value: 'order_existing_tags', type: 'list' },
  { label: 'Order Total', value: 'order_total', type: 'number' },
  { label: 'Order Tracking Number', value: 'order_tracking_numbers', type: 'text_list' },
  { label: 'Order Weight (oz)', value: 'order_weight', type: 'number' },
  { label: 'Shipping Country', value: 'order_shipping_country', type: 'text' },
  { label: 'Shipping Name', value: 'order_customer_name', type: 'text' },
  { label: 'Shipping State', value: 'order_shipping_state', type: 'text' },
  { label: 'Store Name', value: 'order_store_name', type: 'text' },
  { label: 'Store Type', value: 'order_store_type', type: 'text' },
];

export const FIELD_TYPE_MAP = {};
FIELD_OPTIONS.forEach((f) => { FIELD_TYPE_MAP[f.value] = f.type; });

// Operator display labels — spec Table 3
export const OPERATOR_LABELS = {
  equals: 'equals',
  not_equals: 'does not equal',
  contains: 'contains',
  not_contains: 'does not contain',
  starts_with: 'starts with',
  ends_with: 'ends with',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
  greater_than: 'is greater than',
  less_than: 'is less than',
  greater_or_equal: 'is greater than or equal to',
  less_or_equal: 'is less than or equal to',
  between: 'is between',
  is: 'is',
  is_not: 'is not',
  is_in_list: 'is in list',
  is_present: 'is present',
  is_not_present: 'is not present',
  not_starts_with: 'does not start with',
  all_present: 'All are present',
  any_not_present: 'Any are not present',
};

export const SKU_MULTI_OPERATOR_LABELS = {
  is_present:     'Any are present',
  is_not_present: 'None are present',
  all_present:    'All are present',
  any_not_present:'Any are not present',
};

export const SKU_PICKER_OPERATORS = ['is_present', 'is_not_present', 'all_present', 'any_not_present'];
export const SKU_MULTI_TO_SINGLE_OPERATOR = {
  all_present:    'is_present',
  any_not_present:'is_not_present',
};

// Operators by field type — corrected for CelBuilder compatibility
export const OPERATORS_BY_TYPE = {
  text: ['equals', 'not_equals', 'contains', 'starts_with', 'ends_with', 'is_empty', 'is_not_empty'],
  number: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_or_equal', 'less_or_equal', 'between'],
  list: ['contains', 'not_contains', 'is_empty', 'is_not_empty'],
  status: ['is', 'is_not', 'is_in_list'],
};

export const FIELD_OPERATORS_OVERRIDE = {
  order_item_skus: ['is_present', 'is_not_present', 'all_present', 'any_not_present'],
  order_tracking_numbers: ['is_present', 'is_not_present', 'starts_with', 'not_starts_with'],
  order_shipping_methods: ['equals', 'not_equals', 'contains', 'not_contains'],
  order_custom:           ['equals', 'not_equals', 'contains', 'not_contains'],
  order_item_custom:      ['equals', 'not_equals', 'contains', 'not_contains'],
  order_item_sale_prices: ['less_than', 'greater_than', 'less_or_equal', 'greater_or_equal'],
  order_item_categories:  ['is_present', 'is_not_present'],
};

// Known order statuses for status dropdown
export const PARTIALLY_SCANNED_SENTINEL = '__partially_scanned';

export const STATUS_OPTIONS = [
  { label: 'Awaiting',          value: 'awaiting' },
  { label: 'Scanned',           value: 'scanned' },
  { label: 'Cancelled',         value: 'cancelled' },
  { label: 'Service Issue',     value: 'serviceissue' },
  { label: 'Action Required',   value: 'onhold' },
  { label: 'Partially Scanned', value: PARTIALLY_SCANNED_SENTINEL },
];

// Operators that require no value input
export const NO_VALUE_OPERATORS = ['is_empty', 'is_not_empty'];

const sortByLabel = (a, b) => a.label.localeCompare(b.label);

export const COUNTRY_OPTIONS = allCountries
  .map((c) => ({ value: c[1], label: `${c[0]} (${c[1]})` }))
  .sort(sortByLabel);

const _usRegions = (allCountries.find((c) => c[1] === 'US')?.[2]) || [];
export const STATE_OPTIONS_US = _usRegions
  .map((s) => ({ value: s[1], label: `${s[0]} (${s[1]})` }))
  .sort(sortByLabel);

const _caRegions = (allCountries.find((c) => c[1] === 'CA')?.[2]) || [];
export const STATE_OPTIONS_CA = _caRegions
  .map((s) => ({ value: s[1], label: `${s[0]} (${s[1]})` }))
  .sort(sortByLabel);

// Action types — MVP only
export const ACTION_TYPE_OPTIONS = [
  { label: 'Add Tag', value: 'add_tag' },
  { label: 'Add Item', value: 'add_item' },
];

// Get operator options for a field type
export function getOperatorsForType(fieldType) {
  const operators = OPERATORS_BY_TYPE[fieldType] || [];
  return operators.map((op) => ({
    value: op,
    label: OPERATOR_LABELS[op],
  }));
}

export function getOperatorsForField(fieldValue, fieldType) {
  const override = FIELD_OPERATORS_OVERRIDE[fieldValue];
  const operators = override || OPERATORS_BY_TYPE[fieldType] || [];
  return operators.map((op) => ({
    value: op,
    label: OPERATOR_LABELS[op],
  }));
}

export function getSkuOperatorOptions(skuCount) {
  if (skuCount > 1) {
    return SKU_PICKER_OPERATORS.map((op) => ({
      value: op,
      label: SKU_MULTI_OPERATOR_LABELS[op],
    }));
  }
  return [
    { value: 'is_present',     label: OPERATOR_LABELS.is_present },
    { value: 'is_not_present', label: OPERATOR_LABELS.is_not_present },
  ];
}

// Starter examples — spec §8.2 Component 8
export const STARTER_EXAMPLES = [
  {
    title: 'Tag high-value orders',
    description: 'Add "high-value" tag when order total > $100',
    conditions_json: [{ field: 'order_total', operator: 'greater_than', value: 100 }],
    actions_json: [{ type: 'add_tag', value: { tag_name: 'high-value' } }],
    logic: 'all',
  },
  {
    title: 'Flag international shipments',
    description: 'Add "international" tag for non-US orders',
    conditions_json: [{ field: 'order_shipping_country', operator: 'not_equals', value: 'US' }],
    actions_json: [{ type: 'add_tag', value: { tag_name: 'international' } }],
    logic: 'all',
  },
  {
    title: 'Add gift wrap for VIP',
    description: 'Add GIFT-WRAP item when order has "VIP" tag',
    conditions_json: [{ field: 'order_existing_tags', operator: 'contains', value: 'VIP' }],
    actions_json: [{ type: 'add_item', value: { sku: 'GIFT-WRAP', quantity: 1, name: 'Gift Wrap' } }],
    logic: 'all',
  },
];
